import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { buildQueryKey } from "@/utils/query";
import type { IAPI } from "../APIService";
import type { ISalesLimit } from "./SalesLimits";
import type { IAllergen, IDietaryRequirement } from "./Context";
import type { ThemeColour } from "@/components/screens/Operations/Types/Operations";

export interface IItem {
  allergens: IAllergen[];
  master_menu_item_id: number | null;
  theme_color: string | null | ThemeColour;
  active: boolean;
  collection_vat_amount: number;
  display_order: number;
  on_premise_vat_applicable: boolean;
  internal_name: string | null;
  in_stock: boolean;
  collection_vat_applicable: boolean;
  updated_at: string;
  alt_name: string | null;
  item_type: number;
  display_name: string;
  is_promoted: boolean | null;
  number_of_modifier_categories: number;
  discount_applicable: boolean;
  number_of_categories: number;
  created_at: string;
  display_image: string;
  name: string;
  promotion_value: number | null;
  order: number;
  dietary_requirements: IDietaryRequirement[];
  display_description: string;
  needs_preparation: boolean;
  cost_price: number;
  value: number;
  id: number;
  sales_limit?: ISalesLimit;
  stock_level?: ItemStockLevel;
  on_premise_vat_amount: number;
  allow_custom_amount: boolean;
}

export enum ItemStockLevel {
  // biome-ignore lint/style/useLiteralEnumMembers: <Need All to be null>
  All = null,
  OutOfStock = 0,
  InStock = 1,
  LimitedStock = 2,
}

export enum ItemType {
  // biome-ignore lint/style/useLiteralEnumMembers: <explanation>
  All = null,
  Food = 0,
  Drink = 1,
  Other = 2,
}

export enum ItemOrderByParams {
  Name = "name",
  NumberOfCategories = "number_of_categories",
  NumberOfModifierSets = "number_of_modifier_categories",
  Active = "active",
  InStock = "in_stock",
  ItemType = "item_type",
  Value = "value",
  Order = "order",
}

export interface IListItemsParams {
  page?: number;
  search?: string;
  stock_level?: ItemStockLevel;
  stock?: ItemStockLevel | boolean;
  active?: boolean;
  item_type?: ItemType | boolean;
  uncategorised?: boolean;
  order_by?: ItemOrderByParams | string;
  page_size?: number;
  menu_category_id?: number;
  configuration_id?: ID;
}

export interface IItemsListResponse extends APIPaginatedResponse<IItem> {}

export interface IListItemsReponseAll {
  pages: IItemsListResponse[];
  pageParams: number[];
}

export interface ICreateItemParams {
  name: string;
  display_name: string;
  value: number;
  item_type: ItemType;
  theme_color?: string | null | ThemeColour;
  active?: boolean;
  in_stock?: boolean;
  promotion_value?: number;
  internal_name?: string;
  is_promoted?: boolean;
  display_description?: string;
  allow_custom_amount?: boolean;
  order?: number;
  display_order?: number;
  cost_price?: number;
  allergens?: number[];
  dietary_requirements?: number[];
  needs_preparation?: boolean;
  discount_applicable?: boolean;
  collection_vat_amount?: number;
  on_premise_vat_amount?: number;
  menucategory_set?: number[];
  display_image?: string | null;
}

export interface IBulkCreateItemRelationshipsParams {
  menu_item_id?: ID;
  menu_item_menu_categories?: Array<{
    menu_item_id: number | string;
    menu_category_id: number | string;
  }>;
  modifier_category_menu_items?: Array<{
    menu_item_id: number | string;
    modifier_category_id: number | string;
  }>;
  modifier_modifier_categories?: Array<{
    modifier_category_id: number | string;
    modifier_id: number | string;
  }>;
}

export interface IBulkCreateItemRelationshipsResponse {
  modifier_modifier_categories: Array<{
    cost_price: null;
    id: number;
    is_default_option: null;
    order: null;
    modifier_id: number;
    value: null;
    modifier_category_id: number;
  }>;
  modifier_category_menu_items: Array<{
    selection_required: null;
    id: number;
    order: null;
    modifier_category_id: number;
    menu_item_id: number;
  }>;
  menu_item_menu_categories: Array<{
    cost_price: null;
    id: number;
    promotion_value: null;
    order: null;
    value: null;
    menu_item_id: number;
    is_promoted: null;
    menu_category_id: number;
  }>;
}

export type IFormattedItem = Omit<
  IItem,
  "allergens" | "dietary_requirements"
> & {
  allergens: number[];
  dietary_requirements: number[];
  low_stock_limit: number | null;
  item_count: number | null;
  has_sales_limit: boolean;
};

export interface IListCategoriesRelationshipsParams {
  menu_item_id: ID;
}

interface IListCategoriesRelationship {
  is_promoted: null | boolean;
  id: number;
  menu_item_id: number;
  menu_category_id: number;
  menu_item: {
    item_type: number;
    number_of_categories: number | null;
    is_promoted: null | boolean;
    number_of_modifier_categories: number;
    id: number;
    display_image: string;
    in_stock: boolean;
    order: number;
    active: boolean;
    name: string;
    display_description: string;
    promotion_value: null | number;
    value: number;
  };
  order: number;
  menu_category: {
    id: number;
    has_time_restrictions: boolean;
    order: number;
    active: boolean;
    visibility: number;
    name: string;
    number_of_items: number;
  };
  promotion_value: null | number;
  value: number;
  cost_price: number;
}

export interface IListCategoriesRelationshipsResponse
  extends APIPaginatedResponse<IListCategoriesRelationship> {}

export interface IListModifierSetsRelationshipsParams {
  menu_item_id: ID;
}

export interface IListModifierSetsRelationship {
  modifier_category_id: number;
  menu_item: {
    active: boolean;
    number_of_modifier_categories: number;
    promotion_value: number;
    order: number;
    in_stock: boolean;
    display_image: string;
    stock_level: number;
    allow_custom_amount: boolean;
    name: string;
    is_promoted: boolean;
    display_description: string;
    value: number;
    number_of_categories: number;
    id: number;
    item_type: number;
  };
  menu_item_id: number;
  order: number;
  selection_required: boolean;
  modifier_category: {
    active: boolean;
    internal_name: string;
    order: number;
    selection_required: boolean;
    number_of_modifiers: number;
    name: string;
    category_type: number;
    id: number;
  };
  id: number;
}

export interface IListModifierSetsRelationshipsResponse
  extends APIPaginatedResponse<IListModifierSetsRelationship> {}

export interface IDeleteItemCategoryRelationshipParamsBase {
  relationship_id: ID;
  menu_item_id: ID;
}

export interface IDeleteItemModifierSetRelationshipParamsBase {
  relationship_id: ID;
  menu_item_id: ID;
}

export interface IUpdateItemParams extends ICreateItemParams {
  id: ID;
}

export interface IDeleteItemParams {
  menu_item_id: ID;
}

export interface ISearchOrganisationItemsParams {
  search: string;
  page?: number;
  page_size?: number;
  package_id: ID;
  venue_id?: Array<ID>;
  isEnabled?: boolean;
  max_pages?: number;
  item_type?: Array<ItemType>;
  amount_min?: number;
  amount_max?: number;
  category_id?: Array<number | string>;
}

export interface ISearchOrganisationItem {
  name: string;
  id: number;
  venue_name: string;
  value: number;
  metadata: {
    belongs_to_package: boolean;
  };
  category_names: Array<string>;
}

export interface ISearchOrganisationItemsResponse
  extends APIPaginatedResponse<ISearchOrganisationItem> {}

export interface ISearchAllOrganisationItemsResponseAll {
  pages: Array<ISearchOrganisationItemsResponse>;
  pageParams: Array<number>;
}

export interface IItemsService {
  listAll: (
    params: IListItemsParams,
  ) => UseInfiniteQueryResult<IListItemsReponseAll, Error>;
  list: (params: IListItemsParams) => UseQueryResult<IItemsListResponse, Error>;
  searchOrganisationItems: (
    params: ISearchOrganisationItemsParams,
  ) => UseQueryResult<ISearchOrganisationItemsResponse, Error>;
  searchAllOrganisationItems: (
    params: ISearchOrganisationItemsParams,
  ) => UseInfiniteQueryResult<ISearchAllOrganisationItemsResponseAll, Error>;
  create: () => UseMutationResult<IItem, Error, ICreateItemParams>;
  update: () => UseMutationResult<IItem, Error, IUpdateItemParams>;
  delete: () => UseMutationResult<boolean, Error, IDeleteItemParams>;
  retrieve: (id: ID) => UseQueryResult<IFormattedItem, Error>;

  // Relationships
  bulkCreateItemRelationships: () => UseMutationResult<
    IBulkCreateItemRelationshipsResponse,
    Error,
    IBulkCreateItemRelationshipsParams
  >;

  // List Relationships
  listCategoriesRelationships: (
    params: IListCategoriesRelationshipsParams,
  ) => UseQueryResult<Array<IListCategoriesRelationship>, Error>;

  listModifierSetsRelationships: (
    params: IListModifierSetsRelationshipsParams,
  ) => UseQueryResult<Array<IListModifierSetsRelationship>, Error>;

  // Delete Relationships
  deleteItemCategoryRelationships: () => UseMutationResult<
    boolean,
    Error,
    IDeleteItemCategoryRelationshipParamsBase
  >;
  deleteItemModifierSetRelationships: () => UseMutationResult<
    boolean,
    Error,
    IDeleteItemModifierSetRelationshipParamsBase
  >;
}

export class ItemsService implements IItemsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  listAll(
    params: IListItemsParams,
  ): UseInfiniteQueryResult<IListItemsReponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null),
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "items", "all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<AxiosResponse<IItemsListResponse>>(
          "/menu/items",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: { ...updatedParams, page: pageParam, page_size: 100 },
          },
        );

        return data;
      },
      getNextPageParam: (lastPage: IItemsListResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  list(params: IListItemsParams): UseQueryResult<IItemsListResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "items"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IItemsListResponse>>(
          "/menu/items",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: updatedParams,
          },
        );

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  searchOrganisationItems(
    params: ISearchOrganisationItemsParams,
  ): UseQueryResult<ISearchOrganisationItemsResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined,
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "items", "search-organisation-items"],
        venueID: null,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<ISearchOrganisationItemsResponse>
        >("/menu/search/items/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
  }

  searchAllOrganisationItems(
    params: ISearchOrganisationItemsParams,
  ): UseInfiniteQueryResult<ISearchAllOrganisationItemsResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null),
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "items", "search-all-organisation-items"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<ISearchOrganisationItemsResponse>
        >("/menu/search/items/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 100 },
        });

        return data;
      },
      getNextPageParam: (lastPage: ISearchOrganisationItemsResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          const nextPage = Number.parseInt(
            url.searchParams.get("page") || "1",
            10,
          );
          if (params.max_pages !== undefined && nextPage > params.max_pages) {
            return undefined;
          }
          return nextPage;
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      gcTime: 0,
      staleTime: 0,
      initialPageParam: 1,
      enabled: params.isEnabled,
    });
  }

  create(): UseMutationResult<IItem, Error, ICreateItemParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "items", "create"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IItem>>(
          "/menu/me/menu_items/",
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: params,
          },
        );

        return data;
      },

      // TODO: Implement Functionality when doing edit menu workflow
      onMutate: async (newMenu) => {},
      onError: (err, newMenu, context) => {},
      onSettled: () => {},
    });
  }

  retrieve(id: ID): UseQueryResult<IFormattedItem, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "items", "retrieve", id],
        venueID: null,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IItem>>(
          `/menu/items/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );

        const formattedData: IFormattedItem = {
          ...data,
          allergens: data.allergens.map((allergen) => allergen.id),
          dietary_requirements: data.dietary_requirements.map(
            (requirement) => requirement.id,
          ),
          low_stock_limit: null,
          item_count: null,
          has_sales_limit: false,
        };

        if (data.stock_level === ItemStockLevel.LimitedStock) {
          const { data: salesLimitData } = await this.api.get<
            AxiosResponse<ISalesLimit>
          >(`/sales_limits/${id}/`, {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          });

          formattedData.low_stock_limit = salesLimitData.low_stock_limit;
          formattedData.item_count = salesLimitData.item_count;
          formattedData.has_sales_limit = true;
        }

        return {
          ...formattedData,
        };
      },
    });
  }

  update(): UseMutationResult<IItem, Error, IUpdateItemParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "items", "update"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IItem>>(
          `/menu/me/menu_items/${id}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: restParams,
          },
        );

        return data;
      },
      onMutate: async (newItem) => {},
      onError: (err, newItem, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "items",
            "retrieve",
            variables.id.toString(),
          ],
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteItemParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "items", "delete"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/menu_items/${params.menu_item_id}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: {},
          },
        );

        return true;
      },
      onMutate: async (newDeleteItemParams) => {},
      onError: (err, newDeleteItemParams, context) => {
        return false;
      },
      onSettled: () => {},
    });
  }

  // Relationship Mutations
  bulkCreateItemRelationships(): UseMutationResult<
    IBulkCreateItemRelationshipsResponse,
    Error,
    IBulkCreateItemRelationshipsParams
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "items", "bulkCreateItemRelationships"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { menu_item_id, ...restParams } = params;
        const { data } = await this.api.post<
          AxiosResponse<IBulkCreateItemRelationshipsResponse>
        >("/menu/me/relationships/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });

        return data;
      },
      onMutate: async (newBulkCreateItemRelationshipsParams) => {},
      onError: (err, newBulkCreateItemRelationshipsParams, context) => {},
      onSettled: (data, error, variables) => {
        if (variables.menu_item_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "items", "listCategoriesRelationships"],
          });
        }
      },
    });
  }

  // List Relationships
  listCategoriesRelationships(
    params: IListCategoriesRelationshipsParams,
  ): UseQueryResult<Array<IListCategoriesRelationship>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "items", "listCategoriesRelationships"],
        venueID: null,
        params: {
          ...params,
          page_size: 100,
        },
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListCategoriesRelationshipsResponse>
        >("/menu/me/menuitem_menucategories/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...params,
            page_size: 100,
          },
        });

        return data.results;
      },
    });
  }

  listModifierSetsRelationships(
    params: IListModifierSetsRelationshipsParams,
  ): UseQueryResult<Array<IListModifierSetsRelationship>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "items", "listModifierSetsRelationships"],
        venueID: null,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListModifierSetsRelationshipsResponse>
        >("/menu/me/modifiercategory_menuitems/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...params,
            page: 1,
            page_size: 50,
          },
        });

        return data.results;
      },
    });
  }

  // Delete Relationships
  deleteItemCategoryRelationships(): UseMutationResult<
    boolean,
    Error,
    IDeleteItemCategoryRelationshipParamsBase
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "items", "deleteItemRelationships"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/menuitem_menucategories/${params.relationship_id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );

        return true;
      },
      onMutate: async (newDeleteRelationshipParams) => {},
      onError: (err, newDeleteRelationshipParams, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        if (variables.menu_item_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "items", "listCategoriesRelationships"],
          });
        }
      },
    });
  }

  deleteItemModifierSetRelationships(): UseMutationResult<
    boolean,
    Error,
    IDeleteItemModifierSetRelationshipParamsBase
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "items", "deleteItemModifierSetRelationships"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/modifiercategory_menuitems/${params.relationship_id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );

        return true;
      },
      onMutate: async (newDeleteRelationshipParams) => {},
      onError: (err, newDeleteRelationshipParams, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        if (variables.menu_item_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "items", "listModifierSetsRelationships"],
          });
        }
      },
    });
  }
}
